/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  DropdownList,
  DropdownUserAssistance,
  EmptyResults,
  LinkItem,
  SelectMobileDropdown,
  preventDefault
} from '#PRIVATE_SelectCommon';
import type { DataState } from '#UNSAFE_Collection';
import { Label } from '#UNSAFE_Label';
import { Separator } from '#UNSAFE_Separator';
import { TextFieldInput } from '#UNSAFE_TextField';
import { InlineUserAssistance, type UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { View } from '#UNSAFE_View';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import type { Item } from '#utils/UNSAFE_dataProvider';
import type { ItemTextType } from '#utils/UNSAFE_selectUtils';
import type { Handle } from '@oracle/oraclejet-internal-utilities/typeUtils';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import type { ComponentChildren, ComponentProps, RefObject } from 'preact';
import type { Dispatch, StateUpdater } from 'preact/hooks';
import { MobileDropdownFooter } from './MobileDropdownFooter';
import type { CollectionRendererProps } from './SelectMultiple.types';
import { SelectMultipleField } from './SelectMultipleField';
import { SelectMultipleFieldInput } from './SelectMultipleFieldInput';
import { TextTagList } from './TextTagList';
import { useMobileDropdown } from './useMobileDropdown';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;
type SelectMultipleFieldInputProps = ComponentProps<typeof SelectMultipleFieldInput>;
type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type TextTagListHandle = Handle<typeof TextTagList, 'ref'>;

type Props<K extends string | number, D extends Record<string, any>> = {
  'aria-describedby'?: string;
  'aria-label'?: string;
  assistiveText?: string;
  collectionRenderer: (props: CollectionRendererProps<K, D>) => ComponentChildren;
  data?: DataState<K, D> | null;
  dropdownId: string;
  enabledElementRef: RefObject<HTMLElement>;
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  isAddToListShown?: boolean;
  isDisabled?: boolean;
  isEmptyResults: boolean;
  isFocused: boolean;
  isLoading: boolean;
  isOpen: boolean;
  isReadonly?: false;
  isRequired?: boolean;
  isSelectedOnlyView: boolean;
  itemText: ItemTextType<K, D>;
  label: string;
  liveRegionText?: string;
  placeholder?: TextFieldInputProps['placeholder'];
  propIsLoading?: boolean;
  selectedValuesDescriptionId?: string;
  setDropdownOpen: Dispatch<StateUpdater<boolean>>;
  setUserInput: Dispatch<StateUpdater<string | undefined>>;
  stopFiltering: () => void;
  textAlign?: TextFieldInputProps['textAlign'];
  textTagListRef: RefObject<TextTagListHandle>;
  userAssistanceDensity?: UserAssistanceDensityType;
  userInput?: string;
  valueItems?: Item<K, D>[];
  virtualKeyboard?: SelectMultipleFieldInputProps['virtualKeyboard'];
  onAddToListAction?: () => void;
  onCommit: (detail: ValueUpdateDetail<Set<K>>) => void;
  onFieldBlur?: (event: FocusEvent) => void;
  onFieldFocus?: (event: FocusEvent) => void;
  onFieldInput?: SelectMultipleFieldInputProps['onInput'];
  onFieldKeyDown?: SelectMultipleFieldInputProps['onKeyDown'];
  onFieldKeyUp?: SelectMultipleFieldInputProps['onKeyUp'];
};

export function SelectMultipleMobileDropdown<
  K extends string | number,
  D extends Record<string, any>
>({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  assistiveText,
  collectionRenderer,
  data,
  dropdownId,
  enabledElementRef,
  helpSourceLink,
  helpSourceText,
  isAddToListShown,
  isDisabled,
  isEmptyResults,
  isFocused,
  isLoading,
  isOpen,
  isReadonly,
  isRequired,
  isSelectedOnlyView: propIsSelectedOnlyView,
  itemText,
  label,
  liveRegionText,
  placeholder,
  propIsLoading,
  selectedValuesDescriptionId,
  setDropdownOpen,
  setUserInput,
  stopFiltering,
  textAlign,
  textTagListRef,
  userAssistanceDensity,
  userInput,
  valueItems,
  virtualKeyboard,
  onAddToListAction,
  onCommit,
  onFieldBlur,
  onFieldFocus,
  onFieldInput,
  onFieldKeyDown,
  onFieldKeyUp
}: Props<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const {
    collectionRendererContext,
    footerProps: mobileDropdownFooterProps,
    selectMultipleFieldInputProps: mobileDropdownSelectMultipleFieldInputProps,
    selectMultipleFieldProps: mobileDropdownSelectMultipleFieldProps,
    selectedValuesCountProps: mobileDropdownSelectedValueCountProps
  } = useMobileDropdown<K, D>({
    data,
    inputRef: enabledElementRef,
    isDropdownOpen: isOpen,
    isDropdownSelectedOnlyView: propIsSelectedOnlyView,
    itemText,
    textTagListRef,
    userInput,
    valueItems,
    onCommit,
    setDropdownOpen,
    setUserInput,
    stopFiltering
  });

  // for dropdown field
  const {
    formFieldContext: mobileDropdownFormFieldContext,
    inputProps: mobileDropdownInputProps,
    labelProps: mobileDropdownLabelProps,
    textFieldProps: mobileDropdownTextFieldProps,
    userAssistanceProps: mobileDropdownUserAssistanceProps
  } = useTextField({
    ariaDescribedBy,
    helpSourceLink,
    helpSourceText,
    isDisabled: false,
    isFocused,
    isLoading,
    isReadonly: false,
    isRequiredShown: false,
    labelEdge: 'inside',
    styleVariant: 'default',
    value: mobileDropdownSelectMultipleFieldProps.selectedValuesCount > 0 ? true : undefined
  });

  // JET-54256 - inside label animates up on initial display with selected value
  // If we're waiting for the selected values to load, the component should look like it
  // has a value in the meantime.
  // Use the original propIsLoading instead of isLoading because we don't want to delay
  // the inside label position by a timer like we do the progressive loading indicator.
  mobileDropdownFormFieldContext.hasValue =
    propIsLoading || mobileDropdownFormFieldContext.hasValue;

  const mobileDropdownFieldLabelProps = {
    label: <Label {...mobileDropdownLabelProps}>{label}</Label>,
    labelEdge: 'inside' as const
  };

  const dropdownInlineUserAssistance =
    isOpen &&
    !isDisabled &&
    !isReadonly &&
    (assistiveText || helpSourceLink) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <DropdownUserAssistance
        assistiveText={assistiveText}
        fieldLabel={label}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        userAssistanceDensity={userAssistanceDensity}
        {...mobileDropdownUserAssistanceProps}
      />
    ) : undefined;

  const dropdownListContent = isEmptyResults ? (
    isAddToListShown ? undefined : (
      <EmptyResults />
    )
  ) : (
    collectionRenderer(collectionRendererContext)
  );

  const addToListContent = isAddToListShown ? (
    <>
      <div onMouseDown={preventDefault}>
        <Separator />
      </div>
      <LinkItem onAction={onAddToListAction}>{translations.select_addToList()}</LinkItem>
    </>
  ) : undefined;

  return (
    <SelectMobileDropdown
      header={
        <FormFieldContext.Provider value={mobileDropdownFormFieldContext}>
          <View>
            <SelectMultipleField
              fieldLabel={label}
              onBlur={onFieldBlur}
              onFocus={onFieldFocus}
              {...mobileDropdownFieldLabelProps}
              {...mobileDropdownTextFieldProps}
              {...mobileDropdownSelectMultipleFieldProps}
              {...mobileDropdownSelectedValueCountProps}>
              <SelectMultipleFieldInput
                aria-controls={dropdownId}
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                isAddToListShown={isAddToListShown}
                isRequired={isRequired}
                liveRegionText={liveRegionText}
                placeholder={placeholder}
                selectedValuesDescriptionId={selectedValuesDescriptionId}
                textAlign={textAlign}
                virtualKeyboard={virtualKeyboard}
                onInput={onFieldInput}
                onKeyDown={onFieldKeyDown}
                onKeyUp={onFieldKeyUp}
                {...mobileDropdownInputProps}
                {...mobileDropdownSelectMultipleFieldInputProps}
              />
            </SelectMultipleField>
          </View>
          {dropdownInlineUserAssistance}
          {addToListContent}
        </FormFieldContext.Provider>
      }
      footer={<MobileDropdownFooter {...mobileDropdownFooterProps} />}
      id={dropdownId}
      isOpen={isOpen}
      labelId={mobileDropdownLabelProps.id}>
      <DropdownList hasTopGap={!addToListContent} hasBottomGap={true} isLoading={data === null}>
        {dropdownListContent}
      </DropdownList>
    </SelectMobileDropdown>
  );
}
